import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  CustomerCompanyGroupsState,
  CompanyGroupsQuery,
  CompanyGroupsQueryParameter,
  CompanyGroup,
  CompanyGroupItemsQueryParameter,
  ChangeCompanyGroup,
  CompanyGroupItemsQuery
} from "./models/models";
import customerCompanyGroupsApi from "./api/customerCompanyGroupsApi";

export const getCompanyGroups = createAsyncThunk(
  "customerCompanyGroups/getGroups",
  async (query: CompanyGroupsQuery) => {
    return customerCompanyGroupsApi.getGroups(query);
  }
);

export const getCompanyGroup = createAsyncThunk(
  "customerCompanyGroups/getGroup",
  async (id: string) => {
    return customerCompanyGroupsApi.getGroup(id);
  }
);

export const createCompanyGroup = createAsyncThunk(
  "customerCompanyGroups/createGroup",
  async (model: ChangeCompanyGroup) => {
    return customerCompanyGroupsApi.createGroup(model);
  }
);

export const changeCompanyGroup = createAsyncThunk(
  "customerCompanyGroups/changeGroup",
  async ({ id, model }: any) => {
    return customerCompanyGroupsApi.changeGroup(id, model);
  }
);

export const deleteCompanyGroup = createAsyncThunk(
  "customerCompanyGroups/deleteGroup",
  async ({ id, model }: any) => {
    return customerCompanyGroupsApi.deleteGroup(id);
  }
);

export const getCompanyGroupItems = createAsyncThunk(
  "customerCompanyGroups/getGroupItems",
  async ({ id, query }: { id: string, query: CompanyGroupItemsQuery }) => {
    return customerCompanyGroupsApi.getGroupItems(id, query);
  }
);

export const addCompanyGroupItem = createAsyncThunk(
  "customerCompanyGroups/addGroupItem",
  async ({ id, customerId }: { id: string; customerId: string }) => {
    return customerCompanyGroupsApi.addGroupItem(id, customerId);
  }
);

export const deleteCompanyGroupItem = createAsyncThunk(
  "customerCompanyGroups/deleteGroupItem",
  async ({ id, itemId }: { id: string, itemId: string }) => {
    return customerCompanyGroupsApi.deleteGroupItem(id, itemId);
  }
);

const initialState: CustomerCompanyGroupsState = {
  query: {
    search: undefined,
    sortBy: CompanyGroupsQueryParameter.name,
    sortDesc: false,
    pageNo: 1,
    pageSize: 20
  },
  companyGroups: undefined,
  companyGroup: undefined,
  companyGroupItemsQuery: {
    sortBy: CompanyGroupItemsQueryParameter.name,
    sortDesc: false,
    pageNo: 1,
    pageSize: 20
  },
  companyGroupItems: undefined,
  loading: false,
  success: undefined,
  error: undefined
};

const customerCompanyGroupsSlice = createSlice({
  name: "customerCompanyGroups",
  initialState: initialState,
  reducers: {
    changeQuery: (state, action) => {
      state.query = action.payload;
    },
    selectCompanyGroup: (state, action) => {
      state.companyGroup = action.payload;
    },
    changeCompanyGroupItemsQuery: (state, action) => {
      state.companyGroupItemsQuery = action.payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getCompanyGroups.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCompanyGroups.fulfilled, (state, { payload }) => {
        state.companyGroups = payload;
        state.loading = false;
      })
      .addCase(getCompanyGroups.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getCompanyGroup.fulfilled, (state, { payload }) => {
        state.companyGroup = payload;
      })
      .addCase(getCompanyGroup.rejected, (state, { error }) => {
        state.error = error;
      });

    builder
      .addCase(createCompanyGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCompanyGroup.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(createCompanyGroup.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(changeCompanyGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeCompanyGroup.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(changeCompanyGroup.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(deleteCompanyGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCompanyGroup.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(deleteCompanyGroup.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getCompanyGroupItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCompanyGroupItems.fulfilled, (state, { payload }) => {
        state.companyGroupItems = payload;
        state.loading = false;
      })
      .addCase(getCompanyGroupItems.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(deleteCompanyGroupItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCompanyGroupItem.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(deleteCompanyGroupItem.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });
  }
});

export const {
  changeQuery,
  selectCompanyGroup,
  changeCompanyGroupItemsQuery,
  setSuccess,
  setLoading,
  setError
} = customerCompanyGroupsSlice.actions;

export default customerCompanyGroupsSlice.reducer;